import { useEffect, useMemo, useState } from 'react';
import { useUser } from '../components/UserContext';
import { fetchMyBranding } from '../api/branding';

// Per-user persisted branding, so an authenticated surface (dashboard/portal)
// paints the org logo INSTANTLY on the next load instead of flashing Surelia
// while the live source loads. Keyed by uid so nothing leaks across users and
// the pre-auth login page (no uid) never shows a cached brand.
const uidCacheKey = (uid) => `branding:uid:${uid}`;

function readUidBranding(uid) {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(uidCacheKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeUidBranding(uid, branding) {
  if (!uid) return;
  try {
    if (branding) localStorage.setItem(uidCacheKey(uid), JSON.stringify(branding));
    else localStorage.removeItem(uidCacheKey(uid));
  } catch (_) {
    // localStorage may be unavailable; ignore.
  }
}

// Default (Surelia) look. `isWhiteLabel` false means "use the native Surelia
// gradient + palette"; a white-label org overrides the logo and the two colors.
export const DEFAULT_BRANDING = {
  logoUrl: '/logo-blanco-verde.png',
  nombreComercial: 'Surelia',
  colorPrimario: null,
  colorSecundario: null,
  logoBgWhite: false,
  isWhiteLabel: false,
};

// Perceived luminance (0-255) of a #rrggbb color; null for anything else.
function hexLuminance(hex) {
  if (typeof hex !== 'string') return null;
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * True when a branded background (one or two hex colors, e.g. the ends of a
 * gradient) is light enough that white text would be unreadable on it.
 * Invalid/missing colors are ignored; no valid color means "not light".
 */
export function isLightBackground(...hexes) {
  const lums = hexes.map(hexLuminance).filter((l) => l !== null);
  if (!lums.length) return false;
  const avg = lums.reduce((a, b) => a + b, 0) / lums.length;
  return avg > 186;
}

// --- WCAG color math for brand-derived text colors ---

function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

// WCAG relative luminance (0..1) — the one contrast ratios are defined over.
function relLuminance({ r, g, b }) {
  const f = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

function contrastRatio(l1, l2) {
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

function mixRgb(a, b, t) {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

const rgbCss = ({ r, g, b }) => `rgb(${r}, ${g}, ${b})`;

/**
 * Derive text colors for a branded gradient background programmatically:
 * start from the brand color itself and push it toward black or white
 * (whichever direction has more contrast headroom) just until it clears
 * WCAG contrast against BOTH ends of the gradient. The letters keep a
 * subtle tint of the org's brand instead of flipping to plain white/black.
 *
 * Returns { heading, body, muted, isLight } as CSS color strings, or null
 * when no valid color is given (caller keeps its default styling).
 * `isLight` is true when the background is light (dark text was chosen).
 */
export function getBrandTextColors(fromHex, toHex) {
  const from = hexToRgb(fromHex) || hexToRgb(toHex);
  const to = hexToRgb(toHex) || hexToRgb(fromHex);
  if (!from || !to) return null;

  const lumFrom = relLuminance(from);
  const lumTo = relLuminance(to);
  // Worst-case contrast: text must hold up over both ends of the gradient.
  const minContrast = (lum) => Math.min(contrastRatio(lumFrom, lum), contrastRatio(lumTo, lum));

  const towardWhite = minContrast(1) >= minContrast(0);
  const target = towardWhite ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 };
  const seed = from; // colorPrimario carries the brand hue

  // Smallest mix toward the target that clears the ratio; falls back to
  // pure black/white (always passes for any bg that made minContrast pick it).
  const pick = (minRatio) => {
    for (let t = 0.55; t <= 1.0001; t += 0.05) {
      const c = mixRgb(seed, target, t);
      if (minContrast(relLuminance(c)) >= minRatio) return c;
    }
    return target;
  };

  const heading = pick(4.8);
  const body = pick(4.5);
  // Muted: let the background bleed in a little, but never below 3:1.
  let muted = mixRgb(body, mixRgb(from, to, 0.5), 0.35);
  if (minContrast(relLuminance(muted)) < 3) muted = body;

  return {
    heading: rgbCss(heading),
    body: rgbCss(body),
    muted: rgbCss(muted),
    isLight: !towardWhite,
  };
}

// Hostnames that are never an org slug.
const RESERVED_SUBDOMAINS = new Set(['app', 'www', 'demo', 'localhost']);

/**
 * Extract an org slug from the current hostname, e.g.
 * "inventa.surelia.mx" -> "inventa". Returns null for the main app domain,
 * reserved names, localhost, or anything that isn't a single-level
 * subdomain of surelia.mx.
 */
export function slugFromHostname() {
  const host = window.location.hostname;
  const suffix = '.surelia.mx';
  if (!host.endsWith(suffix)) return null;
  const sub = host.slice(0, -suffix.length);
  if (!sub || sub.includes('.')) return null;
  if (RESERVED_SUBDOMAINS.has(sub)) return null;
  return sub;
}

/**
 * Turn a raw branding object (from orgData.branding, the link payload, or the
 * public /branding endpoint) into display-ready values with defaults filled
 * in. Pass null/undefined to get the default Surelia branding.
 */
export function resolveBranding(raw) {
  if (!raw || typeof raw !== 'object') return DEFAULT_BRANDING;
  return {
    logoUrl: raw.logoUrl || DEFAULT_BRANDING.logoUrl,
    nombreComercial: raw.nombreComercial || DEFAULT_BRANDING.nombreComercial,
    colorPrimario: raw.colorPrimario || null,
    colorSecundario: raw.colorSecundario || null,
    logoBgWhite: raw.logoBgWhite === true,
    isWhiteLabel: true,
  };
}

/**
 * Resolve the active branding for the LOGIN + DASHBOARD chrome.
 *
 * Resolution order:
 *   1. Authenticated org branding from UserContext (live via onSnapshot).
 *      This ALWAYS applies once logged in — the dashboard shows the org's
 *      logo with or without a subdomain, because post-auth we know the org.
 *   2. Pre-auth only: the subdomain slug -> GET /branding/<slug> (cached in
 *      sessionStorage). This is the only way the LOGIN page (no logged-in
 *      org) can know which brand to show, so login is subdomain-gated.
 *   3. Default Surelia branding.
 *
 * (The public document-reception page does NOT use this hook — it brands
 * itself from the link's org via resolveBranding, so clients always see the
 * org's brand there, subdomain or not.)
 */
export function useBranding() {
  const { user, orgData, userData } = useUser();
  const slug = slugFromHostname();
  // brandingEnabled is the admin kill-switch: when off, stored branding must
  // not render (matches the backend's read-path gate).
  const orgBranding = (orgData?.brandingEnabled && orgData.branding) || null;
  const [remoteBranding, setRemoteBranding] = useState(null);
  const [clientBranding, setClientBranding] = useState(null);
  const [clientFetched, setClientFetched] = useState(false);

  // Client users never load orgData (Firestore rules block reading the org
  // doc), so fetch their org branding from the backend instead. This is what
  // brands the client portal's sidebar logo, subdomain or not.
  useEffect(() => {
    if (userData?.rol !== 'client' || !userData?.org) return;

    let cancelled = false;
    fetchMyBranding()
      .then((branding) => {
        if (cancelled) return;
        setClientBranding(branding);
        setClientFetched(true);
      })
      .catch(() => {
        // Transient failure (network, auth blip): stay "unknown" so the
        // cached brand keeps rendering instead of flashing Surelia.
      });

    return () => {
      cancelled = true;
    };
  }, [userData?.rol, userData?.org]);

  useEffect(() => {
    // If we already have branding from the authenticated org, no fetch needed.
    if (orgBranding) return;
    // Off a subdomain there is nothing to resolve pre-auth — Surelia look.
    if (!slug) return;

    const cacheKey = `branding:${slug}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        setRemoteBranding(JSON.parse(cached));
        return;
      } catch (_) {
        // fall through to refetch
      }
    }

    let cancelled = false;
    const apiBase = process.env.REACT_APP_API_URL;
    fetch(`${apiBase}/branding/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data || !data.branding) return;
        setRemoteBranding(data.branding);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(data.branding));
        } catch (_) {
          // sessionStorage may be unavailable; ignore.
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [slug, orgBranding]);

  // Determine the authoritative branding for this render, and whether we've
  // actually finished loading it (`known`). `resolved` may be null even when
  // known (org simply has no branding).
  let resolved = null;
  let known = false;
  if (orgBranding) {
    resolved = orgBranding;
    known = true;
  } else if (orgData) {
    // Broker: org doc loaded but no branding → authoritatively Surelia.
    known = true;
  } else if (userData?.rol === 'client' && clientFetched) {
    resolved = clientBranding;
    known = true;
  } else if (slug && remoteBranding) {
    resolved = remoteBranding;
    known = true;
  }

  // Last-known branding for THIS user, read synchronously for an instant first
  // paint while the live source is still loading.
  const cachedForUser = useMemo(() => readUidBranding(user?.uid), [user?.uid]);

  // Background refresh: once we know the authoritative branding, persist it
  // (or clear it) so the next load paints instantly. Writing only on change
  // (resolvedKey dep) is the "trigger" — a different logo updates the cache.
  const resolvedKey = resolved ? JSON.stringify(resolved) : '';
  useEffect(() => {
    if (!user?.uid || !known) return;
    writeUidBranding(user.uid, resolved);
  }, [user?.uid, known, resolvedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live authoritative branding wins. While still loading, fall back to this
  // user's cached branding (no Surelia flash). Pre-auth (no uid) never uses the
  // cache, so the login page shows Surelia unless resolved via subdomain.
  if (resolved) return resolveBranding(resolved);
  if (known) return DEFAULT_BRANDING;
  if (user?.uid && cachedForUser) return resolveBranding(cachedForUser);
  if (!slug) return DEFAULT_BRANDING;
  return resolveBranding(remoteBranding);
}
